/**
 * Result of parsing the `product_mid_tier_program` feature-flag value into the list of enrolled
 * Mid Tier Program parent IDs. `error` is populated (and `parentIds` left empty) when the value is
 * present but not a well-formed JSON array of strings, so callers can surface a warning.
 */
type MidTierProgramParseResult = {
  parentIds: string[];
  error?: string;
};

/**
 * Parses the raw Flagsmith remote-config value for `product_mid_tier_program` into the enrolled
 * parent IDs. An absent or blank value yields an empty list with no error (the feature can be
 * enabled before any parents are enrolled); a malformed value yields an empty list plus an `error`.
 */
export const parseMidTierProgramParentIds = (
  value: string | number | boolean | null | undefined
): MidTierProgramParseResult => {
  if (value == null) {
    return { parentIds: [] };
  }
  if (typeof value !== 'string') {
    return { parentIds: [], error: `Expected a JSON string but received type "${typeof value}"` };
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return { parentIds: [] };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { parentIds: [], error: 'Value is not valid JSON' };
  }

  if (!Array.isArray(parsed)) {
    return { parentIds: [], error: 'Value is not a JSON array' };
  }

  const entries: unknown[] = parsed;
  const stringEntries = entries.filter((entry): entry is string => typeof entry === 'string');
  if (stringEntries.length !== entries.length) {
    return { parentIds: [], error: 'Value contains non-string entries' };
  }

  const parentIds = stringEntries.map((entry) => entry.trim().toUpperCase()).filter((entry) => entry !== '');
  return { parentIds };
};
